import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response';
import { SUPPRESS_TOAST_HEADER, SUPPRESS_AUTH_REDIRECT_HEADER } from '../interceptors/http-context';

interface PublicConfig {
  paymobEnabled: boolean;
}

/**
 * Loads public app config from /api/v1/config once at bootstrap and exposes it as signals.
 * Falls back to safe defaults (paymobEnabled = true) if the endpoint is unreachable, so that
 * existing payment flows keep working even without a config response.
 *
 * Uses HttpClient directly (not ApiService) and suppresses the global toast / auth redirect to
 * avoid noise during bootstrap and to keep this service free of cross-deps.
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly http = inject(HttpClient);

  private readonly _paymobEnabled = signal<boolean>(true);
  readonly paymobEnabled = this._paymobEnabled.asReadonly();

  load(): Promise<void> {
    const url = `${environment.apiUrl}/config`;
    const headers: Record<string, string> = {
      [SUPPRESS_TOAST_HEADER]: '1',
      [SUPPRESS_AUTH_REDIRECT_HEADER]: '1',
    };
    return firstValueFrom(this.http.get<ApiResponse<PublicConfig>>(url, { headers }))
      .then(res => {
        if (res?.success && res.data && typeof res.data.paymobEnabled === 'boolean') {
          this._paymobEnabled.set(res.data.paymobEnabled);
        }
      })
      .catch(() => {
        // Fail-safe: keep default (true). Existing flows still work.
      });
  }
}
